import { withConformance, registerResponsiveContract } from '@norkron/web-conformance';
import { forwardRef } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { registerCodes } from '@norkron/web-errors';
import { registerLazy } from '@norkron/web-conformance/register-lazy';

// src/Skeleton.tsx

// src/version.ts
var LIB_VERSION = "0.1.1";
var WEB_SKELETON_LIB = "@norkron/web-skeleton";
function toCssLength(value) {
  if (value == null) return void 0;
  return typeof value === "number" ? `${value}px` : value;
}
function joinClassNames(...names) {
  return names.filter(Boolean).join(" ");
}
var Skeleton = forwardRef(function Skeleton2({
  variant = "text",
  width,
  height,
  animation = "pulse",
  inheritColor = false,
  className,
  style,
  children,
  ...rest
}, ref) {
  const merged = { ...style };
  const w = toCssLength(width);
  const h = toCssLength(height);
  if (w !== void 0) merged.width = w;
  if (h !== void 0) merged.height = h;
  const classes = joinClassNames(
    "skeleton",
    `skeleton--variant-${variant}`,
    `skeleton--animation-${animation}`,
    inheritColor && "skeleton--inherit-color",
    className
  );
  const pillEllipseOptOut = variant === "circle" ? { "data-allow-pill-ellipse": "true" } : {};
  return /* @__PURE__ */ jsx(
    "span",
    {
      ref,
      role: "status",
      "aria-busy": "true",
      "aria-live": "polite",
      "data-variant": variant,
      "data-animation": animation,
      className: classes,
      style: merged,
      ...pillEllipseOptOut,
      ...withConformance(WEB_SKELETON_LIB, LIB_VERSION),
      ...rest,
      children
    }
  );
});
var SkeletonText = forwardRef(function SkeletonText2({
  lines = 3,
  lineHeight = "1em",
  lastLineWidth = "60%",
  animation = "pulse",
  inheritColor = false,
  className,
  ...rest
}, ref) {
  const count = Math.max(1, Math.floor(lines));
  const lineEls = [];
  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1;
    lineEls.push(
      /* @__PURE__ */ jsx(
        Skeleton,
        {
          variant: "text",
          animation,
          inheritColor,
          height: lineHeight,
          width: isLast && count > 1 ? lastLineWidth : "100%",
          className: "skeleton-text__line"
        },
        i
      )
    );
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      role: "status",
      "aria-busy": "true",
      "data-lines": count,
      className: joinClassNames("skeleton-text", className),
      ...withConformance(WEB_SKELETON_LIB, LIB_VERSION),
      ...rest,
      children: lineEls
    }
  );
});
var SkeletonAvatar = forwardRef(
  function SkeletonAvatar2({ size = 40, className, ...rest }, ref) {
    return /* @__PURE__ */ jsx(
      Skeleton,
      {
        ref,
        variant: "circle",
        width: size,
        height: size,
        className: joinClassNames("skeleton-avatar", className),
        ...rest
      }
    );
  }
);
var SkeletonCard = forwardRef(function SkeletonCard2({
  variant = "image-top",
  animation = "pulse",
  inheritColor = false,
  lines = 3,
  className,
  ...rest
}, ref) {
  const showMedia = variant !== "text-only";
  const lineCount = Math.max(1, Math.floor(lines));
  const lineEls = [];
  for (let i = 0; i < lineCount; i++) {
    const isLast = i === lineCount - 1;
    lineEls.push(
      /* @__PURE__ */ jsx(
        Skeleton,
        {
          variant: "text",
          animation,
          inheritColor,
          width: isLast && lineCount > 1 ? "70%" : "100%",
          className: "skeleton-card__line"
        },
        i
      )
    );
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      role: "status",
      "aria-busy": "true",
      "data-variant": variant,
      className: joinClassNames("skeleton-card", `skeleton-card--${variant}`, className),
      ...withConformance(WEB_SKELETON_LIB, LIB_VERSION),
      ...rest,
      children: [
        showMedia && /* @__PURE__ */ jsx(
          Skeleton,
          {
            variant: "rounded",
            animation,
            inheritColor,
            className: "skeleton-card__media"
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "skeleton-card__body", children: [
          /* @__PURE__ */ jsx(
            Skeleton,
            {
              variant: "text",
              animation,
              inheritColor,
              className: "skeleton-card__title"
            }
          ),
          lineEls
        ] })
      ]
    }
  );
});
var SkeletonList = forwardRef(function SkeletonList2({ rows = 5, rowHeight = "2.5rem", animation = "pulse", inheritColor = false, className, ...rest }, ref) {
  const count = Math.max(1, Math.floor(rows));
  const rowEls = [];
  for (let i = 0; i < count; i++) {
    rowEls.push(
      /* @__PURE__ */ jsx(
        Skeleton,
        {
          variant: "rounded",
          animation,
          inheritColor,
          height: rowHeight,
          className: "skeleton-list__row"
        },
        i
      )
    );
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      role: "status",
      "aria-busy": "true",
      "data-rows": count,
      className: joinClassNames("skeleton-list", className),
      ...withConformance(WEB_SKELETON_LIB, LIB_VERSION),
      ...rest,
      children: rowEls
    }
  );
});
var SkeletonTable = forwardRef(function SkeletonTable2({ rows = 4, cols = 4, animation = "pulse", inheritColor = false, className, style, ...rest }, ref) {
  const rowCount = Math.max(1, Math.floor(rows));
  const colCount = Math.max(1, Math.floor(cols));
  const rowEls = [];
  for (let r = 0; r < rowCount; r++) {
    const cells = [];
    for (let c = 0; c < colCount; c++) {
      cells.push(
        /* @__PURE__ */ jsx(
          Skeleton,
          {
            variant: "rect",
            animation,
            inheritColor,
            className: "skeleton-table__cell"
          },
          c
        )
      );
    }
    rowEls.push(
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "skeleton-table__row",
          style: { gridTemplateColumns: `repeat(${colCount}, 1fr)` },
          children: cells
        },
        r
      )
    );
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      role: "status",
      "aria-busy": "true",
      "data-rows": rowCount,
      "data-cols": colCount,
      className: joinClassNames("skeleton-table", className),
      style,
      ...withConformance(WEB_SKELETON_LIB, LIB_VERSION),
      ...rest,
      children: rowEls
    }
  );
});
var SkeletonStat = forwardRef(function SkeletonStat2({ animation = "pulse", inheritColor = false, className, ...rest }, ref) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      role: "status",
      "aria-busy": "true",
      className: joinClassNames("skeleton-stat", className),
      ...withConformance(WEB_SKELETON_LIB, LIB_VERSION),
      ...rest,
      children: [
        /* @__PURE__ */ jsx(
          Skeleton,
          {
            variant: "text",
            animation,
            inheritColor,
            className: "skeleton-stat__label"
          }
        ),
        /* @__PURE__ */ jsx(
          Skeleton,
          {
            variant: "rounded",
            animation,
            inheritColor,
            className: "skeleton-stat__value"
          }
        )
      ]
    }
  );
});
var SkeletonBlogPost = forwardRef(
  function SkeletonBlogPost2({ animation = "pulse", inheritColor = false, lines = 4, className, ...rest }, ref) {
    const count = Math.max(1, Math.floor(lines));
    const lineEls = [];
    for (let i = 0; i < count; i++) {
      const isLast = i === count - 1;
      lineEls.push(
        /* @__PURE__ */ jsx(
          Skeleton,
          {
            variant: "text",
            animation,
            inheritColor,
            width: isLast && count > 1 ? "55%" : "100%",
            className: "skeleton-blog-post__line"
          },
          i
        )
      );
    }
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        role: "status",
        "aria-busy": "true",
        className: joinClassNames("skeleton-blog-post", className),
        ...withConformance(WEB_SKELETON_LIB, LIB_VERSION),
        ...rest,
        children: [
          /* @__PURE__ */ jsx(
            Skeleton,
            {
              variant: "rounded",
              animation,
              inheritColor,
              className: "skeleton-blog-post__media"
            }
          ),
          /* @__PURE__ */ jsx(
            Skeleton,
            {
              variant: "text",
              animation,
              inheritColor,
              className: "skeleton-blog-post__title"
            }
          ),
          lineEls,
          /* @__PURE__ */ jsx(
            Skeleton,
            {
              variant: "text",
              animation,
              inheritColor,
              className: "skeleton-blog-post__meta"
            }
          )
        ]
      }
    );
  }
);
var SkeletonProfile = forwardRef(
  function SkeletonProfile2({ avatarSize = 48, animation = "pulse", inheritColor = false, className, ...rest }, ref) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        role: "status",
        "aria-busy": "true",
        className: joinClassNames("skeleton-profile", className),
        ...withConformance(WEB_SKELETON_LIB, LIB_VERSION),
        ...rest,
        children: [
          /* @__PURE__ */ jsx(
            SkeletonAvatar,
            {
              size: avatarSize,
              animation,
              inheritColor,
              className: "skeleton-profile__avatar"
            }
          ),
          /* @__PURE__ */ jsxs("div", { style: { flex: "1 1 auto", display: "flex", flexDirection: "column", gap: "0.4rem" }, children: [
            /* @__PURE__ */ jsx(
              Skeleton,
              {
                variant: "text",
                animation,
                inheritColor,
                className: "skeleton-profile__name"
              }
            ),
            /* @__PURE__ */ jsx(
              Skeleton,
              {
                variant: "text",
                animation,
                inheritColor,
                className: "skeleton-profile__line"
              }
            ),
            /* @__PURE__ */ jsx(
              Skeleton,
              {
                variant: "text",
                animation,
                inheritColor,
                className: "skeleton-profile__line"
              }
            )
          ] })
        ]
      }
    );
  }
);
var WEB_SKELETON_LIB_ID = "@norkron/web-skeleton";
try {
  registerCodes(WEB_SKELETON_LIB_ID, {
    WEB_SKELETON_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck: the per-call budget (default 4ms) was exceeded while walking `.skeleton` elements. Partial results are returned; the remaining elements were not checked. (Conformance severity: info.)",
      severity: "info",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-skeleton/WEB_SKELETON_SELFCHECK_BUDGET_EXCEEDED",
      exampleFix: "Pass a larger `budgetMs` via selfCheck opts, or reduce the number of mounted skeletons in a single subtree."
    },
    WEB_SKELETON_MISSING_ARIA_BUSY: {
      description: 'selfCheck: a `.skeleton` element is mounted but neither it nor any ancestor has `aria-busy="true"`. Assistive tech cannot detect the loading state. (Conformance severity: major.)',
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-skeleton/WEB_SKELETON_MISSING_ARIA_BUSY",
      exampleFix: 'Set `aria-busy="true"` on the skeleton element itself or on a containing region. The base <Skeleton> primitive does this by default; composites that strip the attribute must re-add it on a wrapping element.'
    },
    WEB_SKELETON_ANIMATING_DESPITE_REDUCED_MOTION: {
      description: 'selfCheck: a skeleton is animating (data-animation !== "none") while the user agent advertises `prefers-reduced-motion: reduce`. The lib must opt out of motion under that media query. (Conformance severity: major.)',
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-skeleton/WEB_SKELETON_ANIMATING_DESPITE_REDUCED_MOTION",
      exampleFix: 'Pass `animation="none"` to <Skeleton>, OR wrap the keyframes in `@media (prefers-reduced-motion: no-preference)`.'
    },
    WEB_SKELETON_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck: an internal error was caught and surfaced as a non-throwing finding (selfCheck never throws). Indicates a defect in the selfCheck itself, not in consumer code. (Conformance severity: info.)",
      severity: "info",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-skeleton/WEB_SKELETON_SELFCHECK_INTERNAL_ERROR",
      exampleFix: "File an issue against @norkron/web-skeleton with the finding.message stack context."
    }
  });
} catch {
}

// src/self-check.ts
var LIB_ID = "@norkron/web-skeleton";
var DEFAULT_BUDGET_MS = 50;
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function parseOpts(opts) {
  const budget = opts?.budgetMs;
  const sev = opts?.minSeverity;
  const validSev = ["critical", "major", "minor", "info"];
  return {
    budgetMs: typeof budget === "number" && Number.isFinite(budget) && budget > 0 ? budget : DEFAULT_BUDGET_MS,
    minSeverity: sev !== void 0 && validSev.includes(sev) ? sev : "info"
  };
}
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function now() {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}
function ancestorHasAriaBusy(el) {
  let cur = el;
  while (cur) {
    if (cur.getAttribute("aria-busy") === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}
function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches === true;
  } catch {
    return false;
  }
}
function selfCheck(root, opts) {
  const start = now();
  const safeOpts = parseOpts(opts);
  const findings = [];
  const reduced = prefersReducedMotion();
  try {
    const skeletons = Array.from(root.querySelectorAll(".skeleton"));
    if (skeletons.length === 0) return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
    for (const el of skeletons) {
      if (now() - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_SKELETON_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now() - start }
        });
        break;
      }
      if (!ancestorHasAriaBusy(el)) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_SKELETON_MISSING_ARIA_BUSY",
          category: "a11y",
          severity: "major",
          message: 'A `.skeleton` element is mounted but neither it nor any ancestor has `aria-busy="true"`. Assistive tech cannot detect the loading state.',
          selector: selectorFor(el),
          suggestedFix: 'Set `aria-busy="true"` on the skeleton element itself or on a containing region. The base <Skeleton> primitive does this by default; composites that strip the attribute must re-add it on a wrapping element.'
        });
      }
      if (reduced) {
        const anim = el.getAttribute("data-animation");
        if (anim !== null && anim !== "none") {
          findings.push({
            libId: LIB_ID,
            code: "WEB_SKELETON_ANIMATING_DESPITE_REDUCED_MOTION",
            category: "a11y",
            severity: "major",
            message: `Skeleton is animating (data-animation="${anim}") while user agent advertises prefers-reduced-motion: reduce. Disable animation under the @media (prefers-reduced-motion: reduce) query.`,
            selector: selectorFor(el),
            suggestedFix: 'Pass `animation="none"` to <Skeleton>, OR ensure the lib stylesheet wraps the keyframes in `@media (prefers-reduced-motion: no-preference)`.',
            extras: { animation: anim }
          });
        }
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "WEB_SKELETON_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-skeleton",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/index.ts
registerLazy(
  "@norkron/web-skeleton",
  () => selfCheck
);

export { LIB_VERSION, RESPONSIVE_CONTRACT, Skeleton, SkeletonAvatar, SkeletonBlogPost, SkeletonCard, SkeletonList, SkeletonProfile, SkeletonStat, SkeletonTable, SkeletonText, WEB_SKELETON_LIB, WEB_SKELETON_LIB_ID, selfCheck };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map